import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowDown } from "lucide-react";
import { FadeIn } from "@/components/common/FadeIn";
import { SEO } from "@/components/common/SEO";
import { ArtworkModal } from "@/components/common/ArtworkModal";
import { NotFound } from "@/components/common/NotFound";
import { SITE_URL } from "@/lib/seo-config";
import api from "@/lib/api";

// Section order: THE WORK → BACKGROUND → RECOGNITION (biographical context before accolades).
// Optional sections are only rendered when non-empty.
const BIO_SECTION_ORDER = [
  { field: 'the_work',    label: 'The Work' },
  { field: 'background',  label: 'Background' },
  { field: 'recognition', label: 'Recognition' },
];

// Per-artist overrides (keyed by slug) for gallery-curated ordering.
const BIO_SECTION_ORDER_BY_SLUG = {
  'sheldon-harvey': [
    { field: 'background',  label: 'Background' },
    { field: 'the_work',    label: 'The Work' },
    { field: 'recognition', label: 'Recognition' },
  ],
};

function splitParagraphs(text) {
  if (!text) return [];
  // Paragraphs are separated by one or more blank lines; also tolerate single-\n lists.
  return text.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);
}

function formatMedium(medium) {
  if (!medium) return '';
  return medium.split(/,\s*|&\s*|\s+and\s+/).map(s => s.trim()).filter(Boolean).join(' \u00B7 ');
}

export default function ArtistDetail() {
  const { slug, pieceSlug } = useParams();
  const navigate = useNavigate();
  const [artist, setArtist] = useState(null);
  const [loadState, setLoadState] = useState("loading"); // "loading" | "ok" | "not_found" | "error"
  const [selectedArtwork, setSelectedArtwork] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoadState("loading");
    api.get(`/artists/${slug}`)
      .then((r) => {
        if (cancelled) return;
        setArtist(r.data);
        setLoadState("ok");
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadState(err?.response?.status === 404 ? "not_found" : "error");
      });
    return () => { cancelled = true; };
  }, [slug]);

  // Sync the modal with the URL — opens when /artists/:slug/:pieceSlug
  // matches a real piece, closes when the URL drops the pieceSlug.
  // This makes deep links, browser back/forward, and shareable URLs all work.
  useEffect(() => {
    if (!artist) return;
    if (!pieceSlug) {
      setSelectedArtwork(null);
      return;
    }
    const match = (artist.artworks || []).find((a) => a.slug === pieceSlug);
    setSelectedArtwork(match || null);
  }, [artist, pieceSlug]);

  useEffect(() => {
    if (selectedArtwork) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [selectedArtwork]);

  if (loadState === "not_found" || loadState === "error") {
    return (
      <NotFound
        subject="artist"
        backTo="/artists"
        backLabel="All Artists"
        isError={loadState === "error"}
      />
    );
  }

  if (loadState === "loading" || !artist) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#0A1128] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const lede = (artist.pull_quote || '').trim();
  const sectionOrder = BIO_SECTION_ORDER_BY_SLUG[slug] || BIO_SECTION_ORDER;
  const sections = sectionOrder
    .map(({ field, label }) => ({ label, paragraphs: splitParagraphs(artist[field]) }))
    .filter(s => s.paragraphs.length > 0);

  const scrollToWorks = (e) => {
    e.preventDefault();
    const el = document.getElementById('artist-works');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div data-testid="artist-detail-page" className="pt-20">
      {selectedArtwork ? (
        // Per-piece SEO when a piece modal is open — gives each artwork
        // its own canonical URL, title, OG image, and VisualArtwork JSON-LD
        // for richer Google/social previews and indexable deep links.
        <SEO
          title={`${selectedArtwork.title} by ${artist.name} | Rain Boy Fine Art`}
          description={
            (selectedArtwork.description || `${selectedArtwork.title} by ${artist.name}. ${selectedArtwork.medium || ''}${selectedArtwork.dimensions ? `, ${selectedArtwork.dimensions}` : ''}. Available at Rain Boy Fine Art in Tulsa.`)
              .slice(0, 300)
          }
          path={`/artists/${slug}/${selectedArtwork.slug}`}
          image={selectedArtwork.image}
          type="article"
          breadcrumbs={[
            { name: "Home", path: "/" },
            { name: "Artists", path: "/artists" },
            { name: artist.name, path: `/artists/${slug}` },
            { name: selectedArtwork.title, path: `/artists/${slug}/${selectedArtwork.slug}` },
          ]}
          schemas={[{
            "@context": "https://schema.org",
            "@type": "VisualArtwork",
            name: selectedArtwork.title,
            image: selectedArtwork.image,
            url: `${SITE_URL}/artists/${slug}/${selectedArtwork.slug}`,
            artform: selectedArtwork.medium || artist.medium || "Painting",
            artMedium: selectedArtwork.medium || artist.medium || "",
            description: selectedArtwork.description || "",
            creator: {
              "@type": "Person",
              name: artist.name,
              url: `${SITE_URL}/artists/${slug}`,
            },
            ...(selectedArtwork.dimensions ? { artworkSurface: selectedArtwork.dimensions } : {}),
            ...(selectedArtwork.price && !selectedArtwork.sold ? {
              offers: {
                "@type": "Offer",
                price: selectedArtwork.price,
                priceCurrency: "USD",
                availability: "https://schema.org/InStock",
                url: `${SITE_URL}/artists/${slug}/${selectedArtwork.slug}`,
                seller: { "@type": "ArtGallery", name: "Rain Boy Fine Art", "@id": `${SITE_URL}/#gallery` },
              },
            } : {}),
            ...(selectedArtwork.sold ? {
              offers: { "@type": "Offer", availability: "https://schema.org/SoldOut", priceCurrency: "USD" },
            } : {}),
          }]}
        />
      ) : (
        <SEO
          title={`${artist.name} | Artist at Rain Boy Fine Art | Tulsa`}
          description={
            slug === 'marcus-rivera' ? 'Marcus Rivera is an oil painter represented by Rain Boy Fine Art in Tulsa. View available original works and learn about the artist.'
            : slug === 'sheldon-harvey' ? 'Sheldon Harvey is a multidisciplinary artist working in wood sculpture, acrylic, and oil, represented by Rain Boy Fine Art in Tulsa. View available original works.'
            : `${artist.name} is a ${artist.medium?.toLowerCase() || 'contemporary'} artist represented by Rain Boy Fine Art in Tulsa. View available original works and learn about the artist.`
          }
          path={`/artists/${slug}`}
          image={artist.photo}
          breadcrumbs={[{ name: "Home", path: "/" }, { name: "Artists", path: "/artists" }, { name: artist.name, path: `/artists/${slug}` }]}
          schemas={[{
            "@context": "https://schema.org", "@type": "Person", name: artist.name, jobTitle: "Artist",
            description: ((artist.pull_quote || artist.the_work || '').split(".").slice(0, 2).join(".") + ".").trim(),
            image: artist.photo, url: `https://rainboyfineart.com/artists/${slug}`,
            knowsAbout: artist.medium?.split(/,\s*|&\s*|\s+and\s+/).map(s => s.trim()).filter(Boolean) || [],
            affiliation: { "@type": "ArtGallery", name: "Rain Boy Fine Art", "@id": "https://rainboyfineart.com/#gallery" },
          }]}
        />
      )}
      {/* Header */}
      <section className="py-12 md:py-20 px-6 lg:px-12">
        <div className="max-w-[1280px] mx-auto">
          {/* Back link */}
          <Link
            to="/artists"
            className="group inline-flex items-center gap-2 mb-16"
            style={{ fontFamily: "'Outfit', sans-serif", fontSize: '0.72rem', letterSpacing: '0.28em', textTransform: 'uppercase', color: 'rgba(10, 25, 41, 0.55)', textDecoration: 'none' }}
            data-testid="back-to-artists"
          >
            <ArrowLeft size={13} className="transition-transform duration-300 group-hover:-translate-x-1 group-hover:text-[#CBA365]" />
            <span className="transition-colors duration-300 group-hover:text-[#CBA365]">All Artists</span>
          </Link>

          {/* Mobile header — shown above image on small screens */}
          <div className="lg:hidden mb-8">
            <FadeIn>
              <p className="overline mb-5">Artist Profile</p>
              <h1
                className="section-heading font-heading text-[#0A1128] mb-2"
                style={{ fontSize: 'clamp(2.75rem, 4.5vw, 4rem)', lineHeight: 1 }}
              >
                {artist.name}
              </h1>
              <p
                className="font-heading italic"
                style={{ fontSize: '1.2rem', color: '#0A1128', textTransform: 'none', WebkitTextStroke: '0', textShadow: 'none', letterSpacing: '0' }}
              >
                {formatMedium(artist.medium)}
              </p>
            </FadeIn>
          </div>

          {/* Two-column layout */}
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
            {/* Photo column */}
            <FadeIn>
              <div className="relative flex-shrink-0 w-full max-w-[400px] lg:w-[380px]">
                {/* Gold corner brackets */}
                <div className="absolute -top-3 -left-3 w-[22px] h-[22px] border-t border-l border-[#CBA365]" />
                <div className="absolute -bottom-3 -right-3 w-[22px] h-[22px] border-b border-r border-[#CBA365]" />
                <div className="aspect-[3/4] overflow-hidden">
                  <img src={artist.photo} alt={`Portrait of ${artist.name}, artist at Rain Boy Fine Art`} className="w-full h-full object-cover" />
                </div>
              </div>
            </FadeIn>

            {/* Bio column */}
            <FadeIn delay={0.15}>
              <div className="max-w-[640px]">
                {/* Eyebrow — desktop only */}
                <p className="overline mb-5 hidden lg:block">Artist Profile</p>

                {/* Artist name — desktop only */}
                <h1
                  className="section-heading font-heading text-[#0A1128] mb-2 hidden lg:block"
                  style={{ fontSize: 'clamp(2.75rem, 4.5vw, 4rem)', lineHeight: 1 }}
                >
                  {artist.name}
                </h1>

                {/* Medium line — desktop only */}
                <p
                  className="font-heading italic mb-12 hidden lg:block"
                  style={{ fontSize: '1.2rem', color: '#0A1128', textTransform: 'none', WebkitTextStroke: '0', textShadow: 'none', letterSpacing: '0' }}
                >
                  {formatMedium(artist.medium)}
                </p>

                {/* Lede / pull quote */}
                {lede && (
                  <div
                    className="mb-10 pl-5 border-l border-[#CBA365]"
                    style={{ fontFamily: "'Outfit', sans-serif", fontStyle: 'italic', fontSize: '1.25rem', lineHeight: 1.5, color: '#4A5568', textAlign: 'left' }}
                  >
                    {lede}
                  </div>
                )}

                {/* Themed sections */}
                {sections.map((section, si) => (
                  <div key={si} className="mb-11">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-4 h-[1px] bg-[#CBA365]" />
                      <span style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500, fontSize: '0.65rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: '#CBA365' }}>
                        {section.label}
                      </span>
                    </div>
                    <div className="space-y-6">
                      {section.paragraphs.map((p, pi) => (
                        <p key={pi} className="text-[#4A5568] leading-relaxed text-sm md:text-base" style={{ textAlign: 'left' }}>
                          {p}
                        </p>
                      ))}
                    </div>
                  </div>
                ))}

                {/* CTA */}
                {artist.artworks?.length > 0 && (
                  <a
                    href="#artist-works"
                    onClick={scrollToWorks}
                    className="group inline-flex items-center gap-2 mt-2"
                    style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500, fontSize: '0.72rem', letterSpacing: '0.28em', textTransform: 'uppercase', color: '#4A5568', textDecoration: 'none' }}
                    data-testid="view-available-works"
                  >
                    <span className="border-b border-[#CBA365] pb-[2px] transition-colors duration-300 group-hover:text-[#CBA365]">View Available Works</span>
                    <ArrowDown size={13} className="transition-transform duration-300 group-hover:translate-y-[2px] group-hover:text-[#CBA365]" />
                  </a>
                )}
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Artworks */}
      {artist.artworks?.length > 0 && (
        <section id="artist-works" className="bg-[#F8F9FA] py-16 md:py-24 px-6 lg:px-12" data-testid="artist-artworks-section">
          <div className="max-w-7xl mx-auto">
            <FadeIn>
              <p className="overline mb-4">Works</p>
              <h2 className="section-heading font-heading text-4xl sm:text-5xl lg:text-6xl text-[#0A1128] mb-12">
                Available Work
              </h2>
            </FadeIn>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
              {artist.artworks.map((artwork, i) => (
                <FadeIn key={artwork.id}>
                  <button
                    onClick={() => navigate(`/artists/${slug}/${artwork.slug}`)}
                    className="group block text-left w-full cursor-pointer"
                    data-testid={`artist-artwork-${i}`}
                  >
                    <div className="overflow-hidden mb-5 bg-[#F8F9FA]">
                      <img
                        src={artwork.image}
                        alt={`${artwork.title} by ${artist.name}`}
                        className="w-full h-auto object-contain transition-transform duration-700 ease-out group-hover:scale-105"
                      />
                    </div>
                    <div className="transition-transform duration-500 ease-out group-hover:-translate-y-1">
                      <h3 className="font-heading text-lg text-[#0A1128] mb-2 transition-colors duration-300 group-hover:text-[#CBA365] relative inline-block">
                        {artwork.title}
                        <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-[#CBA365] transition-all duration-500 ease-out group-hover:w-full" />
                      </h3>
                      <p className="font-heading italic mb-4" style={{ fontSize: '1.05rem', color: 'rgba(10, 25, 41, 0.75)', textTransform: 'none', WebkitTextStroke: '0.3px currentColor', textShadow: 'none', letterSpacing: '0' }}>
                        {artwork.dimensions}
                      </p>
                      <div>
                        <div className="w-4 h-[1px] bg-[#CBA365] mb-3" />
                        <p style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400, fontSize: '0.85rem', color: 'rgba(10, 25, 41, 0.6)' }}>
                          {artwork.sold ? (
                            <span className="inline-block bg-[#0A1128] text-white text-[10px] uppercase tracking-[0.15em] px-3 py-1">
                              Sold
                            </span>
                          ) : (
                            `$${artwork.price?.toLocaleString()}`
                          )}
                        </p>
                      </div>
                    </div>
                  </button>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Artwork Modal */}
      {selectedArtwork && (
        <ArtworkModal
          artwork={selectedArtwork}
          onClose={() => navigate(`/artists/${slug}`)}
        />
      )}
    </div>
  );
}
